<script lang="ts">
	import { scaleLinear, extent, max, area } from 'd3';
	import Divider from '$lib/components/fragments/Divider.svelte';
	import data from '$lib/data/smoking.csv';
	import { getCopy } from '$lib/lang/copy.svelte';
	import md from '$lib/utils/md';
	import { md as mdScreen } from '$lib/global.svelte';
	import { ScrollTrigger } from 'gsap/ScrollTrigger';

	const copy = $derived(getCopy().tobacco);

	const margin = { top: 10, right: 20, bottom: 30, left: 50 };

	let chartContainer: HTMLElement;
	let containerWidth = $state(300);
	let containerHeight = $state(300);

	const height = $derived(containerHeight - margin.top - margin.bottom);
	const width = $derived(containerWidth - margin.left - margin.right);
	const gap = $derived(mdScreen.current ? 20 : 14);

	let annotationsContainer: HTMLElement;
	let activeAnnotation = $state();

	// Combined derived state to ensure synchronous updates
	const activeData = $derived.by(() => {
		const year = activeAnnotation?.year;
		if (!year) return { year: null, percent: null };

		const d = data.find((d) => d.year === year);
		if (d) return { year, percent: d.percent };

		const sorted = data.slice().sort((a, b) => a.year - b.year);
		for (let i = 0; i < sorted.length - 1; i++) {
			if (year >= sorted[i].year && year <= sorted[i + 1].year) {
				const t = (year - sorted[i].year) / (sorted[i + 1].year - sorted[i].year);
				const percent = sorted[i].percent + t * (sorted[i + 1].percent - sorted[i].percent);
				return { year, percent };
			}
		}
		const percent = year < sorted[0].year ? sorted[0].percent : sorted.at(-1)!.percent;
		return { year, percent };
	});

	const xScale = $derived(
		scaleLinear()
			.domain(extent(data, (d) => d.year) as [number, number])
			.range([0, width])
	);
	const yScale = $derived(
		scaleLinear()
			.domain([0, max(data, (d) => d.percent) as number])
			.range([height, 0])
	);
	const xTicks = $derived(xScale.ticks(10));
	const yTicks = $derived([...yScale.ticks(5), 35]);
	const areaPath = $derived(
		area<(typeof data)[number]>()
			.x((d) => xScale(d.year))
			.y0(height)
			.y1((d) => yScale(d.percent))(data)
	);
</script>

<div class="relative w-full">
	<div
		class="content-well-sm bg-blue sticky top-0 z-20 flex flex-col items-center justify-start border-b-1 border-white"
		bind:clientWidth={containerWidth}
	>
		<div class="mt-4">
			<h2 class="heading-5">{@html md(copy.mini_chart.hed)}</h2>
			<Divider class="my-4" />
			<div class="microcopy-2 flex flex-col gap-1 text-left">
				<p>{@html md(copy.mini_chart.caption)}</p>
			</div>
		</div>

		<!-- CHART -->
		<div
			class="chart-container relative my-4"
			bind:this={chartContainer}
			bind:clientWidth={containerWidth}
			bind:clientHeight={containerHeight}
		>
			<svg width={containerWidth} height={containerHeight} class="block">
				<g transform="translate({margin.left},{margin.top})">
					{#each xTicks as tick (tick)}
						<g transform="translate({xScale(tick)},0)">
							<text x={0} y={height + 25} text-anchor="middle" class="microcopy-2 fill-white">
								{tick}
							</text>
						</g>
					{/each}
					{#each yTicks as percent (percent)}
						<g transform="translate(0,{yScale(percent)})">
							<line
								x1={0}
								x2={width}
								stroke="var(--color-off-white)"
								stroke-opacity="0.5"
								stroke-width="0.5"
							/>
							<text
								x={-15}
								y={0}
								dy="0.32em"
								text-anchor="end"
								class="microcopy-2 fill-white font-bold"
							>
								{percent}%
							</text>
						</g>
					{/each}

					<path d={areaPath} class="fill-red" />

					{#if activeData.year && activeData.percent !== null}
						<g
							class="active-indicator transition-transform duration-300 ease-out"
							style="transform: translateX({xScale(activeData.year)}px)"
						>
							<line
								x1={0}
								x2={0}
								y1={0}
								y2={height}
								stroke-dasharray="4"
								stroke="white"
								opacity="0.8"
							/>
							<circle
								cx={0}
								cy={yScale(activeData.percent)}
								r="6"
								class="fill-yellow stroke-blue stroke-2 transition-all duration-300 ease-out"
							/>
						</g>
					{/if}
				</g>
			</svg>

			{#if activeData.year && activeData.percent !== null}
				<div
					class="bg-yellow absolute z-10 -translate-x-1/2 rounded p-1 text-black transition-all duration-300 ease-out md:p-2"
					style="
						left: {margin.left + xScale(activeData.year)}px;
						top: {margin.top + yScale(activeData.percent) + gap}px;
					"
				>
					<h3 class="heading-5">{Math.round(activeData.percent)}%</h3>
				</div>
			{/if}
		</div>
	</div>

	<!-- ANNOTATIONS -->
	<div class="relative pb-18" bind:this={annotationsContainer}>
		<div class="absolute top-28 bottom-0 left-1/2 z-0 w-px bg-white"></div>
		<div
			class="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 transform rounded-full bg-white"
		></div>
		<div class="relative z-10 my-4">
			<p class="source content-well-sm bg-blue"><i>{@html md(copy.mini_chart.source)}</i></p>
		</div>
		<div class="heading-5 text-yellow mt-6 text-center">
			{copy.mini_chart.prompt}
		</div>
		<div class="relative z-10 flex flex-col items-center gap-12 pt-16">
			{#each copy.annotations as annotation (annotation.year)}
				{@const active = activeAnnotation?.year === annotation.year}
				<div
					class="subtitle-1 mx-4 flex min-h-[150px] max-w-xs flex-col justify-center transition-all duration-300 ease-out md:max-w-md"
					class:scale-110={active}
					{@attach (node: HTMLElement) => {
						ScrollTrigger.create({
							trigger: node,
							start: 'top 65%',
							end: 'bottom 65%',
							onEnter: () => (activeAnnotation = annotation),
							onEnterBack: () => (activeAnnotation = annotation)
						});
					}}
				>
					<p class="text-yellow mb-2 font-bold">{annotation.year}</p>
					<div class="bg-cream rounded-xs p-4 transition-colors" class:bg-gray-dark={!active}>
						<p class="subtitle-2 text-black">{@html md(annotation.text)}</p>
					</div>
				</div>
			{/each}
		</div>
	</div>
</div>

<style lang="postcss">
	@reference '$lib/styles/app.css';

	.chart-container {
		height: min(40vh, 400px);
		min-height: 250px;

		@media (max-width: 768px) {
			height: min(35vh, 300px);
			min-height: 200px;
		}

		@media (max-aspect-ratio: 3/4) {
			height: min(25vh, 250px);
		}
	}
</style>
